import { Check, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ResponsiveDialog } from "@/components/shared/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/language";
import { formatPhoneNumberForDisplay } from "@/lib/phone-utils";
import { cn } from "@/lib/utils";

import type { PickedContact } from "./contacts";

/**
 * Multi-select over the device address book (native path — the web Contact
 * Picker brings its own UI). Search narrows by name or number; the footer
 * button carries the running count.
 */
export function ContactsPickerDialog({
  open,
  contacts,
  onClose,
  onConfirm,
}: {
  open: boolean;
  contacts: PickedContact[];
  onClose: () => void;
  onConfirm: (selected: PickedContact[]) => void;
}) {
  const { t, dir, language } = useLanguage();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Fresh session every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setSelected(new Set());
  }, [open]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return contacts;
    return contacts.filter(
      (contact) =>
        contact.name.toLowerCase().includes(query) ||
        contact.phone.includes(query),
    );
  }, [contacts, search]);

  const toggle = (phone: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  };

  const confirm = () => {
    onConfirm(contacts.filter((contact) => selected.has(contact.phone)));
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      title={t("Mandoob:Import.ContactsPickTitle")}
      footer={
        <Button
          type="button"
          onClick={confirm}
          disabled={selected.size === 0}
          className="tw:w-full"
        >
          {t(
            "Mandoob:Import.ContactsConfirm",
            new Intl.NumberFormat(language === "ar" ? "ar-EH" : "en-US").format(
              selected.size,
            ),
          )}
        </Button>
      }
    >
      <div dir={dir} className="tw:flex tw:min-h-0 tw:flex-col tw:gap-3">
        <div className="tw:relative">
          <Search className="tw:pointer-events-none tw:absolute tw:start-3 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Mandoob:Import.ContactsSearch")}
            className="tw:ps-9"
          />
        </div>
        <div className="tw:min-h-0 tw:max-h-[50dvh] tw:overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="tw:m-0 tw:py-8 tw:text-center tw:text-sm tw:text-muted-foreground">
              {t("Mandoob:Import.ContactsEmpty")}
            </p>
          ) : (
            filtered.map((contact) => {
              const isSelected = selected.has(contact.phone);
              return (
                <button
                  key={contact.phone}
                  type="button"
                  onClick={() => toggle(contact.phone)}
                  aria-pressed={isSelected}
                  className={cn(
                    "tw:flex tw:w-full tw:items-center tw:gap-3 tw:rounded-xl tw:px-3 tw:py-2.5 tw:text-start tw:transition-colors",
                    isSelected ? "tw:bg-brand-50" : "tw:hover:bg-muted/60",
                  )}
                >
                  <span
                    className={cn(
                      "tw:flex tw:size-5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:border tw:transition-colors",
                      isSelected
                        ? "tw:border-brand tw:bg-brand tw:text-brand-foreground"
                        : "tw:border-input",
                    )}
                  >
                    {isSelected && (
                      <Check className="tw:size-3.5" strokeWidth={3} />
                    )}
                  </span>
                  <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
                    <span className="tw:truncate tw:text-sm tw:font-medium tw:text-foreground">
                      <bdi>
                        {contact.name ||
                          formatPhoneNumberForDisplay(contact.phone)}
                      </bdi>
                    </span>
                    {contact.name && (
                      <span
                        dir="ltr"
                        className="tw:truncate tw:text-xs tw:text-muted-foreground"
                        style={{ unicodeBidi: "isolate" }}
                      >
                        {formatPhoneNumberForDisplay(contact.phone)}
                      </span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </ResponsiveDialog>
  );
}
